import { useState, useCallback, useRef } from 'react'
import { medicationsApi, type MedicationsResponse } from '../services/medicationsService'
import type { Medication } from '../types/api'

export interface UseInfiniteAllergiesReturn {
  medications: Medication[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  error: string | null
  loadInitial: () => Promise<void>
  loadMore: () => Promise<void>
  search: (term: string) => Promise<void>
  reset: () => void
}

export function useInfiniteAllergies(pageSize: number = 20): UseInfiniteAllergiesReturn {
  const [medications, setMedications] = useState<Medication[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  
  // Track ongoing requests to prevent duplicates
  const loadingRef = useRef(false)

  const reset = useCallback(() => {
    setMedications([])
    setCurrentPage(1)
    setHasMore(true)
    setError(null)
    setCurrentSearchTerm('')
    loadingRef.current = false
  }, [])

  const fetchMedications = useCallback(async (
    searchTerm: string,
    page: number,
    isLoadMore: boolean = false
  ): Promise<MedicationsResponse | null> => {
    // Prevent duplicate requests
    if (loadingRef.current) {
      return null
    }

    loadingRef.current = true
    
    try {
      setError(null)
      
      if (isLoadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const response = await medicationsApi.getMedicationsPaginated({
        q: searchTerm || undefined, // Don't send empty string
        page_size: pageSize,
        page_number: page
      })

      return response
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load medications'
      setError(errorMessage)
      console.error('Error fetching medications:', err)
      return null
    } finally {
      setLoading(false)
      setLoadingMore(false)
      loadingRef.current = false
    }
  }, [pageSize])

  const loadInitial = useCallback(async () => {
    const response = await fetchMedications('', 1, false)
    if (response) {
      setMedications(response.items)
      setCurrentPage(1)
      setHasMore(response.has_more)
      setCurrentSearchTerm('')
      console.log('[useInfiniteAllergies] Loaded initial medications:', response.items.length)
    }
  }, [fetchMedications])

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingRef.current) {
      return
    }

    const nextPage = currentPage + 1
    const response = await fetchMedications(currentSearchTerm, nextPage, true)
    
    if (response) {
      setMedications(prev => {
        // Prevent duplicates by checking IDs
        const existingIds = new Set(prev.map(med => med.id))
        const newMedications = response.items.filter(med => !existingIds.has(med.id))
        return [...prev, ...newMedications]
      })
      setCurrentPage(nextPage)
      setHasMore(response.has_more)
      console.log('[useInfiniteAllergies] Loaded more medications. Page:', nextPage, 'Count:', response.items.length)
    }
  }, [fetchMedications, hasMore, currentPage, currentSearchTerm])

  const search = useCallback(async (term: string) => {
    const trimmedTerm = term.trim()
    
    // If search term changed, reset pagination
    if (trimmedTerm !== currentSearchTerm) {
      const response = await fetchMedications(trimmedTerm, 1, false)
      
      if (response) {
        setMedications(response.items)
        setCurrentPage(1)
        setHasMore(response.has_more)
        setCurrentSearchTerm(trimmedTerm)
        console.log('[useInfiniteAllergies] Search results for "' + trimmedTerm + '":', response.items.length)
      }
    }
  }, [fetchMedications, currentSearchTerm])

  return {
    medications,
    loading,
    loadingMore,
    hasMore,
    error,
    loadInitial,
    loadMore,
    search,
    reset
  }
}

export default useInfiniteAllergies